import React, { useContext, useState, useEffect } from 'react';
import { get_deployment, get_rs_host, useAPIPost, useDownloadCsv, usePost } from '../API';
import { Checkbox } from '@aidkitorg/component-library';
import { ClickableButton } from '../Components/Button';
import { DateTimeInput } from '../Components/DateTimeInput';
import { ConfigurationContext } from '../Context';
import { useLocalizedStrings } from '../Localization';
import { useRoboScreenerAPI } from '../RoboScreener/useRoboScreenerAPI';
import { SpacedSpinner } from '../Util';
import { FieldDiagnosis } from './FieldDiagnosis';

export function AdminTab(props: { uid: string }) {

  const L = useLocalizedStrings();
  const listReports = usePost('/reports/list');
  const [reports, setReports] = useState<Awaited<ReturnType<typeof listReports>>>([]);
  const downloadReport = usePost("/reports/download_report", { suppressErrorDisplay: true });
  const { downloadCsvData } = useDownloadCsv();
  const [downloading, setDownloading] = useState(false);
  const config = useContext(ConfigurationContext);

  const [key, setKey] = useState('');
  const [value, setValue] = useState('');
  const saveInfo = usePost('/applicant/update');

  useEffect(() => {
    listReports({ report_type: 'applicant' }).then((reports) => {
      setReports(reports || []);
    });
  }, []);

  async function saveKeyValue() {
    if (key.trim() === '') {
      return;
    }
    await saveInfo({ app: props.uid, key, value });
    setKey('');
    setValue('');
  }
    
  // Identity change test (dev only)
  const testIdentityChange = useAPIPost(get_rs_host() + "/compute_and_save", { includeTokenInData: true });
  const [identityTestResult, setIdentityTestResult] = useState<string | null>(null);
  const [identityTestLoading, setIdentityTestLoading] = useState(false);

  // RS Stuff
  const [rsClock, setRSClock] = useState(new Date().toLocaleDateString());
  const [useCustomClock, setUseCustomClock] = useState(false);
  const [gotUpdates, setGotUpdates] = useState(false); 
  const { applicantUpdates, computing, saveUpdates, refreshRS } = useRoboScreenerAPI({ manualOnly: true, uid: props.uid, computeOptions: useCustomClock ? { customClockTimestamp: rsClock } : undefined });

  // Applicant Reports
  async function downloadApplicantReport(report_name: string) {
    setDownloading(true);

    const results = await downloadReport({ name: report_name });

    if (!results || 'error' in results) {
      alert(results.error ?? 'Something went wrong');
      setDownloading(false);
      return;
    }

    downloadCsvData(`${report_name}.csv`, results.results);
    setDownloading(false);
  }

  return (
    <>
      <div className="mb-2 p-2 bg-gray-50 rounded-md">
        <h3 className="text-xl font-bold">{L.applicant.reports.reports_for_this}</h3>
        {reports.length === 0 && <h6>{L.applicant.reports.no_applicant_reports}</h6>}
        <ul>
          {reports.map((report, index: number) => {
            return (
              <li key={`applicant_report_${index}`} className="flex items-center">
                {report.name}
                <div className="ml-2">    
                  <ClickableButton extraClasses="text-xs px-2 py-1 bg-indigo-600 text-white hover:bg-indigo-800" 
                    color="indigo"
                    onClick={() => downloadApplicantReport(report.name)}>
                    {downloading && <SpacedSpinner />}
                    {L.applicant.reports.download}    
                  </ClickableButton>
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      {(config.roles || '').indexOf('admin') !== -1 && (
        <>
          <hr/>
          <div className="rounded-md p-2 my-2 bg-gray-100">
            <h3 className="text-xl font-bold">Key Override</h3>
            <input aria-label="key" placeholder='key' value={key} onChange={(e) => setKey(e.target.value)} />&nbsp;
            <input aria-label="value" placeholder='value' value={value} onChange={(e) => setValue(e.target.value)} />
            <ClickableButton extraClasses="my-2" 
              color="indigo" onClick={saveKeyValue}>Save</ClickableButton>
          </div>
          <FieldDiagnosis uid={props.uid} />
          <div className="rounded-md my-2 p-2 bg-white-50">
            <h3 className="text-xl font-bold">{L.admin.computations.title}</h3>

            <div className="my-2">
              <Checkbox
                label="Use Custom Clock for Testing"
                checked={useCustomClock}
                onChange={(checked) => setUseCustomClock(checked === true)}
              />
            </div>

            <div className={`my-1 rounded-md p-2 border-dashed border-2 ${useCustomClock ? 'border-indigo-200' : 'border-gray-200 bg-gray-50'}`}>
              <DateTimeInput id={'rs-custom-clock-input'} label={"Customize Clock (Optional)"}
                init={'hour'}
                includeTime={true}
                includeReset={true}
                hideLabels={true}
                disabled={!useCustomClock}
                setValue={setRSClock} />
            </div>
            <div className="my-1 mt-3">
              {applicantUpdates && (Object.keys(applicantUpdates[props.uid] || {}) || []).length > 0 ? 
                <>
                  <ClickableButton color="blue" onClick={() => saveUpdates() } disabled={computing}>
                    {computing && <SpacedSpinner />}{L.admin.computations.save_these_updates}
                  </ClickableButton>&nbsp;
                  <ClickableButton color="gray" onClick={async () => { await refreshRS(); setGotUpdates(true); }} disabled={computing}>
                    {computing && <SpacedSpinner />}{L.admin.computations.get_updates}
                  </ClickableButton>
                </> : 
                <ClickableButton color="indigo" onClick={async () => { 
                  console.log("Refreshing RS for applicant: " + props.uid);
                  await refreshRS(); setGotUpdates(true);
                }} disabled={computing}>
                  {computing && <SpacedSpinner />}{L.admin.computations.get_updates}
                </ClickableButton>
              }
            </div>
            <div className="flex-1 pt-1.5 m-2 divide-y grid grid-cols-1 divide-black">
              {gotUpdates && Object.keys(applicantUpdates[props.uid] || {}).length === 0 ? 
                <div className="text-sm">No computed updates for this applicant</div> : <></>}
              {(Object.keys(applicantUpdates[props.uid] || {}) || []).map((key: string) => {
                return <div className="mt-1 w-full h-fit bg-gray-100 break-words p-2 border-4 rounded-md border-black" key={`rs-update-${key}`}>
                  <h3 className="text-sm text-gray-700 font-bold"><var>{key}</var></h3>
                  <data className="text-gray-900">{applicantUpdates[props.uid][key]}</data>
                </div>
              })}
            </div>
          </div>
          {process.env.NODE_ENV === 'development' && (
            <div className="rounded-md p-2 my-2 bg-yellow-50 border border-yellow-300">
              <h3 className="text-xl font-bold">Test Identity Change Protection</h3>
              <p className="text-sm text-gray-600 my-1">
                Attempts to change 3 identity fields at once. Should be blocked by the server.
              </p>
              <ClickableButton color="blue" extraClasses="my-1" disabled={identityTestLoading} onClick={async () => {
                setIdentityTestLoading(true);
                setIdentityTestResult(null);
                try {
                  const result = await testIdentityChange({
                    deploymentKey: get_deployment(),
                    uid: props.uid,
                    changedKeys: {
                      legal_name: 'Test Identity Change ' + Date.now(),
                      phone_number: '0000000000',
                      email: 'test-identity-change@example.com',
                    },
                  });
                  if (result?.error === 'too_many_identity_changes') {
                    setIdentityTestResult('PASS: Server correctly blocked the request.');
                  } else if (result?.error) {
                    setIdentityTestResult(`OTHER ERROR: ${result.error}`);
                  } else {
                    setIdentityTestResult('FAIL: Server allowed the change (this should not happen).');
                  }
                } catch (e) {
                  setIdentityTestResult(`ERROR: ${e}`);
                } finally {
                  setIdentityTestLoading(false);
                }
              }}>
                {identityTestLoading && <SpacedSpinner />}
                Test 3-Field Identity Change
              </ClickableButton>
              {identityTestResult && (
                <div className={`mt-2 p-2 rounded text-sm font-mono ${
                  identityTestResult.startsWith('PASS') ? 'bg-green-100 text-green-800' :
                    identityTestResult.startsWith('FAIL') ? 'bg-red-100 text-red-800' :
                      'bg-gray-100 text-gray-800'
                }`}>
                  {identityTestResult}
                </div>
              )}
            </div>
          )}
        </>)}
    </>
  );
}
